import { Analytics } from '@cobliteam/analytics';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import DropdownMenu from '@cobliteam/ui/components/DropdownMenu';
import Typography from '@cobliteam/ui/components/Typography';

import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import type { Column } from '@tanstack/react-table';
import type React from 'react';

export interface DataTableHeaderProps<T> {
  title?: string;
  align?: 'left' | 'center' | 'right';
  column: Column<T>;
  children?: React.ReactNode;
}

const DataTableHeader = <T,>({ title, column, align = 'left', children }: DataTableHeaderProps<T>) => {
  const { t } = useTranslation('ui-business');
  const showButton = column.getCanHide() || column.getCanSort() || column.getCanPin();
  const alignHeader = column.columnDef.meta?.alignHeader || align;
  const titleHeader = title || column.columnDef.meta?.name;

  const alignHeaderClass = {
    left: '!flex-start !start',
    center: '!flex items-center justify-center  !text-center',
    right: '!flex-end !text-right justify-end !w-fit',
  }[alignHeader];

  if (!showButton) {
    return (
      <div className={cn('p-2', alignHeaderClass)}>
        <Typography color='secondary' variant='description-title'>
          {titleHeader}
        </Typography>
        {children}
      </div>
    );
  }

  return (
    <div className={cn(alignHeaderClass, 'flex min-h-14 w-full items-center py-2')}>
      <DropdownMenu>
        <DropdownMenu.Trigger asChild>
          <div className='flex items-center gap-2'>
            <Button
              variant='ghost'
              size='sm'
              full
              color='default'
              data-testid={`datatable-sort-menu-${column.id}`}
              className={cn(
                'data-[state=open]:inner-border data-[state=open]:inner-border-gray-6 data-[state=open]:border-none data-[state=open]:bg-gray-3 data-[state=open]:text-gray-9',
                'flex h-full w-full items-center justify-between gap-2 whitespace-normal text-balance p-2 text-left font-medium text-gray-9 leading-120 hover:bg-gray-3',
                alignHeaderClass
              )}
            >
              {titleHeader}

              {column.getIsSorted() === 'desc' ? (
                <Icon name='arrow-down' size='sm' className='-mt-0.5 ml-1 shrink-0' />
              ) : column.getIsSorted() === 'asc' ? (
                <Icon name='arrow-up' className='-mt-0.5 ml-1 shrink-0' />
              ) : column.getCanSort() ? (
                <Icon name='chevrons-up-down' className='-mt-0.5 ml-1 shrink-0' />
              ) : null}
            </Button>
            {children}
          </div>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align='start' data-testid='datatable-sort-menu'>
          {column.getCanSort() && (
            <>
              <DropdownMenu.Item onClick={() => column.clearSorting()} disabled={!!column.getIsSorted() === false} data-testid='datatable-sort-clear'>
                <DropdownMenu.Icon icon={<Icon name='chevrons-up-down' />} />
                <DropdownMenu.Label>{t('data_table.clear_sort')}</DropdownMenu.Label>
              </DropdownMenu.Item>
              <DropdownMenu.Item onClick={() => column.toggleSorting(false)} data-testid='datatable-sort-asc'>
                <DropdownMenu.Icon icon={<Icon name='arrow-up' />} />
                <DropdownMenu.Label>{t('data_table.sort_ascending')}</DropdownMenu.Label>
              </DropdownMenu.Item>
              <DropdownMenu.Item onClick={() => column.toggleSorting(true)} data-testid='datatable-sort-desc'>
                <DropdownMenu.Icon icon={<Icon name='arrow-down' />} />
                <DropdownMenu.Label>{t('data_table.sort_descending')}</DropdownMenu.Label>
              </DropdownMenu.Item>
              {column.getCanHide() && <DropdownMenu.Separator />}
            </>
          )}
          {column.getCanPin() && (
            <DropdownMenu.Item
              onClick={() => {
                Analytics.track('DataTable Column Pinned', {
                  column: column.columnDef.meta?.name,
                  pinned: !column.getIsPinned(),
                });
                column.pin(column.getIsPinned() ? false : 'left');
              }}
            >
              <DropdownMenu.Icon icon={<Icon name='pin' />} />
              <DropdownMenu.Label>{column.getIsPinned() ? t('data_table.unpin_column') : t('data_table.pin_column')}</DropdownMenu.Label>
            </DropdownMenu.Item>
          )}
          {column.getCanHide() && (
            <DropdownMenu.Item onClick={() => column.toggleVisibility(false)}>
              <DropdownMenu.Icon icon={<Icon name='eye-off' />} />
              <DropdownMenu.Label>{t('data_table.remove_column')}</DropdownMenu.Label>
            </DropdownMenu.Item>
          )}
        </DropdownMenu.Content>
      </DropdownMenu>
    </div>
  );
};

export default DataTableHeader;
